import { Field, useAddonClasses } from '@flywire/headlessui-tailwindcss';
import { ExclamationIcon } from '@flywire/icons';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import type { CountryForSelect } from '@packages/domain/models/country';
import {
  joinPhoneNumberWithPrefix,
  splitPhoneNumber,
} from '@packages/domain/utils/phone/phone';
import classNames from 'classnames';
import { Keys } from 'constants/keys';
import { useInputAriaProps } from 'hooks/useInputAriaProps/useInputAriaProps';
import { ReactComponent as Arrow } from 'icons/arrow.svg';
import type { ChangeEvent, InputHTMLAttributes, KeyboardEvent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslations } from 'utils/translations/useTranslations';

import './PhoneInput.scss';

type PhoneInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'onChange' | 'onBlur'
> & {
  id: string;
  countries: CountryForSelect[];
  error?: string | boolean;
  help?: string;
  label?: string;
  onBlur?: (id: string) => void;
  onChange?: (id: string, value?: string) => void;
};

const PhoneInput = ({
  countries,
  disabled = false,
  error,
  help,
  hidden,
  id,
  label,
  name,
  placeholder,
  readOnly,
  required,
  type,
  value,
  onBlur,
  onChange,
  ...props
}: PhoneInputProps) => {
  const i18n = useTranslations();
  const dialingCodes = useMemo(
    () =>
      countries
        .filter(({ dialingCode }) => !!dialingCode)
        .map(({ dialingCode }) => dialingCode as string),
    [countries],
  );
  const phone = splitPhoneNumber(value as string, dialingCodes);
  const [prefix, setPrefix] = useState(phone.prefix);
  const [phoneNumber, setPhoneNumber] = useState(phone.phoneNumber);
  const [isFocused, setIsFocused] = useState(false);
  const addonClasses = useAddonClasses({
    position: 'left',
    className: classNames(
      'bg-transparent cursor-pointer inline-block flex items-center justify-center gap-1 focus-visible:ring pl-4',
      prefix.length < 3 ? 'w-24' : 'w-28',
    ),
  });
  const isInvalid = error && typeof error === 'string';
  const placeholderVisible = Boolean(placeholder && !phoneNumber && isFocused);

  const { inputAriaProps } = useInputAriaProps({
    'aria-describedby': placeholderVisible ? `${id}-placeholder` : undefined,
    error: error ?? false,
    label: label || '',
    name: id,
    disabled,
    hint: help,
    readOnly,
    required,
  });

  const onInputKeyDown = (evt: KeyboardEvent<HTMLInputElement>) => {
    const isControl = evt.ctrlKey || evt.metaKey;
    const isCopy = isControl && evt.key === Keys.C;
    const isCut = isControl && evt.key === Keys.X;
    const isNumber = /\d/.test(evt.key);
    const isPaste = isControl && evt.key === Keys.V;
    const allowedChars = [
      Keys.DELETE,
      Keys.ARROW_LEFT,
      Keys.NUMPAD_0,
      Keys.NUMPAD_1,
      Keys.NUMPAD_2,
      Keys.NUMPAD_3,
      Keys.NUMPAD_4,
      Keys.NUMPAD_5,
      Keys.NUMPAD_6,
      Keys.NUMPAD_7,
      Keys.NUMPAD_8,
      Keys.NUMPAD_9,
      Keys.ARROW_RIGHT,
      Keys.SUPR,
      Keys.TAB,
    ].includes(evt.key);

    if (!isNumber && !allowedChars && !isPaste && !isCopy && !isCut) {
      evt.preventDefault();
    }
  };

  const getA11yStatusMessage = (isOpen: boolean) => {
    const isClosed = !isOpen;

    if (isClosed) {
      return i18n.t('phoneInput.results.selected', { label: prefix });
    }

    const resultCount = `${countries.length}`;

    return resultCount === '1'
      ? i18n.t('phoneInput.results.one')
      : i18n.t('phoneInput.results.many', { resultCount });
  };

  const onPrefixChange = (newPrefix: string) => {
    setPrefix(newPrefix);
    onChange?.(id, joinPhoneNumberWithPrefix(newPrefix, phoneNumber));
  };

  const onInputChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const next = ev.target.value || '';
    setPhoneNumber(next);
    onChange?.(id, joinPhoneNumberWithPrefix(prefix, next));
  };

  return (
    <Field id={id}>
      <Field.Group as="div" onBlur={() => onBlur?.(id)} className="relative">
        <Listbox
          as="div"
          className="static"
          value={prefix}
          onChange={onPrefixChange}
          disabled={readOnly || disabled}
        >
          {({ open }) => (
            <>
              <ListboxButton
                className={addonClasses}
                data-testid="phoneInputField-prefix"
                onBlur={() => onBlur?.(id)}
              >
                <span>+{prefix}</span>
                <Arrow
                  className={classNames(
                    'transition-all',
                    open ? 'rotate-180' : 'rotate-0',
                  )}
                />
              </ListboxButton>
              <Options
                countries={countries}
                className="absolute left-0 right-0 z-30 mt-1 max-h-60 list-none overflow-auto rounded-md bg-white p-0 text-base shadow-lg ring-1 ring-gray-500 focus-visible:outline-none"
              />
              <div
                role="status"
                aria-live="polite"
                aria-relevant="additions text"
                className="sr-only"
              >
                {getA11yStatusMessage(open)}
              </div>
            </>
          )}
        </Listbox>

        <div className="relative w-full">
          <Field.Input
            position="right"
            data-testid="phoneInputField-input"
            value={phoneNumber}
            id={id}
            name={name}
            readOnly={readOnly}
            disabled={disabled}
            type={type}
            hidden={hidden}
            pattern="[0-9]+"
            onChange={onInputChange}
            onKeyDown={onInputKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              onBlur?.(id);
            }}
            placeholder={placeholder ? undefined : label}
            required={required}
            {...props}
            {...inputAriaProps}
          />
          {placeholder && !phoneNumber && isFocused && (
            <span
              id={`${id}-placeholder`}
              className="pointer-events-none absolute top-6 z-20 ltr:left-3 rtl:right-3 text-sm text-gray-600"
            >
              {i18n.t(placeholder)}
            </span>
          )}
          {isInvalid && (
            <div className="absolute top-3 ltr:right-3 rtl:left-3">
              <ExclamationIcon className="h-4 w-4 text-red-600" />
            </div>
          )}
          {label && (
            <Field.Label id={`${id}-label`}>
              {label.includes('.') ? i18n.t(label) : label}
            </Field.Label>
          )}
        </div>
      </Field.Group>
      {help && (
        <Field.Hint className="mb-0" data-testid={`${id}-hint`}>
          {help.includes('.') ? i18n.t(help) : help}
        </Field.Hint>
      )}
      {isInvalid && (
        <Field.Error className="mb-0" data-testid={`${id}-error`}>
          {i18n.t(error)}
        </Field.Error>
      )}
    </Field>
  );
};

const Options = ({
  countries,
  className,
}: {
  countries: CountryForSelect[];
  className?: string;
}) => {
  return (
    <ListboxOptions className={className}>
      {countries
        .filter(({ dialingCode }) => !!dialingCode)
        .map((country, index) => (
          <ListboxOption
            key={index}
            value={country.dialingCode}
            className="w-full"
          >
            {({ focus }) => (
              <div
                className={classNames('PhoneInput-option', {
                  'is-active': focus,
                })}
                data-testid={`dial_${country.dialingCode}`}
              >
                <span className="PhoneInput-option-country">
                  {country.label}
                </span>
                <span className="PhoneInput-option-dial">
                  +{country.dialingCode}
                </span>
              </div>
            )}
          </ListboxOption>
        ))}
    </ListboxOptions>
  );
};

export { PhoneInput };
